import React, { useRef, useEffect } from 'react';
import { theme } from 'styles/theme';

const baseStyle = {
  width: '100%',
  height: '100%',
  minHeight: 0,
  padding: '2px 4px',
  boxSizing: 'border-box',
  fontFamily: theme.typography.gridFontFamily,
  fontWeight: 400,
};

/**
 * TanStack 그리드 셀 - 콤보(셀렉트) 인라인 에디터
 * 클릭/스페이스로 편집 진입 시 옵션 목록이 바로 펼쳐짐 (showPicker)
 */
const ComboCellEditor = ({ value = '', options = [], onSave, onCancel, onEnterMoveDown, onEnterMoveUp, onTabMove, onFillSelection }) => {
  const selectRef = useRef(null);
  const aliveRef = useRef(true);
  const graceUntilRef = useRef(0);
  const items = options.map((o) =>
    typeof o === 'object' && o != null && 'value' in o ? o : { value: o, label: String(o) }
  );

  useEffect(() => {
    aliveRef.current = true;
    graceUntilRef.current = Date.now() + 300;
    const el = selectRef.current;
    // showPicker는 일부 브라우저에서 select blur를 유발 → grace 이후에 열어 즉시 종료 방지
    const id = setTimeout(() => {
      try {
        if (typeof el?.showPicker === 'function') el.showPicker();
      } catch (_) { /* NotAllowedError 등 무시 */ }
    }, 80);
    return () => {
      aliveRef.current = false;
      clearTimeout(id);
    };
  }, []);

  const commit = (val, opts) => {
    onSave?.(val, opts);
  };

  return (
    <select
      ref={selectRef}
      defaultValue={value}
      style={baseStyle}
      onChange={(e) => {
        commit(e.target.value, { focusGrid: false });
      }}
      onBlur={() => {
        window.setTimeout(() => {
          if (!aliveRef.current) return;
          if (document.activeElement === selectRef.current) return;
          if (Date.now() < graceUntilRef.current) {
            selectRef.current?.focus();
            return;
          }
          commit(selectRef.current?.value ?? value, { focusGrid: false });
        }, 0);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const val = e.target.value;
          if (e.ctrlKey || e.metaKey) {
            commit(val, { focusGrid: false });
            onFillSelection?.(val);
          } else {
            commit(val, { focusGrid: false });
            if (e.shiftKey) onEnterMoveUp?.();
            else onEnterMoveDown?.();
          }
        }
        if (e.key === 'Tab') {
          e.preventDefault();
          commit(e.target.value, { focusGrid: false });
          onTabMove?.(e.shiftKey ? 'left' : 'right');
        }
        if (e.key === 'Escape') onCancel?.();
      }}
      autoFocus
    >
      {items.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label ?? String(o.value)}
        </option>
      ))}
    </select>
  );
};

export default ComboCellEditor;
